import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Box,
  Button,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/system";
import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";

import Logo from "../../assets/logo-text.png";

import LoginLayout from "./LoginState/LoginLayout";
import styles from "./NavigationBar.module.css"; // Import CSS Module

interface BaseRoute {
  name: string;
}

interface StandardRoute extends BaseRoute {
  path: string;
  hasDropdown?: false;
}

interface DropdownRoute extends BaseRoute {
  hasDropdown: true;
  dropdownItems: Array<{
    path: string;
    name: string;
  }>;
}

type Route = StandardRoute | DropdownRoute;

const buttonStyle: SxProps<Theme> = {
  marginRight: { xs: "8px", sm: "12px", md: "20px" },
  color: "inherit",
  display: "flex",
  alignItems: "center",
  height: "100%",
  fontSize: {
    xs: "0.875rem",
    sm: "1rem",
    md: "1.1rem",
  },
  padding: {
    xs: "6px 8px",
    sm: "8px 12px",
    md: "12px 16px",
  },
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.08)",
  },
};

const toolbarStyle: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  height: {
    xs: "56px",
    sm: "72px",
    md: "96px",
  },
  minHeight: {
    xs: "56px",
    sm: "72px",
    md: "96px",
  },
  px: { xs: 1, sm: 2, md: 3 }, // Add responsive padding
};

const appBarStyle: SxProps<Theme> = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1100,
};

const NavigationBar = (): React.ReactElement => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const dropdownRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>, routeName: string): void => {
    if (!isTablet) {
      setAnchorEl(dropdownRefs.current.get(routeName) || null);
    }
  };

  const handleMouseLeave = (): void => {
    if (!isTablet) {
      setAnchorEl(null);
    }
  };

  const routes: Route[] = [
    { path: "/", name: "Find a Solution" },
    // { path: "/discovery", name: "Discovery" },
    {
      name: "Products",
      hasDropdown: true,
      dropdownItems: [{ path: "/muggleTestV0", name: "Muggle Test" }],
    },
    {
      name: "Creator",
      hasDropdown: true,
      dropdownItems: [
        { path: "/workbench", name: "Agent Workbench" },
        { path: "/evaluation", name: "Agent Evaluation" },
        { path: "/schedule", name: "Scheduled Task" },
      ],
    },
    { path: "/download", name: "Download Studio" },
    { path: "/chat", name: "Ask AI Assistant" },
  ];

  const renderMobileMenuItem = (route: Route): React.ReactElement => {
    if ("hasDropdown" in route && route.hasDropdown) {
      return (
        <React.Fragment key={route.name}>
          <MenuItem
            sx={{
              fontSize: {
                xs: "1rem",
                sm: "1.1rem",
              },
              padding: {
                xs: "10px 16px",
                sm: "12px 24px",
              },
              borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            {route.name}
          </MenuItem>
          {route.dropdownItems.map((item) => (
            <MenuItem
              key={item.path}
              component={Link}
              to={item.path}
              onClick={() => setMobileMenuOpen(false)}
              sx={{
                fontSize: {
                  xs: "0.875rem",
                  sm: "1rem",
                },
                padding: {
                  xs: "8px 24px",
                  sm: "12px 36px",
                },
                backgroundColor: "rgba(255, 255, 255, 0.02)",
              }}
            >
              {item.name}
            </MenuItem>
          ))}
        </React.Fragment>
      );
    }

    return (
      <MenuItem
        key={route.path}
        component={Link}
        to={route.path}
        onClick={() => setMobileMenuOpen(false)}
        sx={{
          fontSize: {
            xs: "1rem",
            sm: "1.1rem",
          },
          padding: {
            xs: "10px 16px",
            sm: "12px 24px",
          },
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        {route.name}
      </MenuItem>
    );
  };

  const renderRoute = (route: Route): React.ReactElement => {
    if (isTablet) {
      return <React.Fragment key={route.name} />;
    }

    if ("hasDropdown" in route && route.hasDropdown) {
      return (
        <Box key={route.name}>
          <Button
            ref={(el) => {
              if (el) {
                dropdownRefs.current.set(route.name, el);
              }
            }}
            sx={buttonStyle}
            onMouseEnter={(e) => handleMouseEnter(e, route.name)}
            endIcon={<KeyboardArrowDownIcon />}
          >
            {route.name}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl) && anchorEl === dropdownRefs.current.get(route.name)}
            onClose={handleMouseLeave}
            MenuListProps={{
              onMouseLeave: handleMouseLeave,
              onMouseEnter: (e) => e.stopPropagation(),
            }}
            sx={{
              "& .MuiPaper-root": {
                backgroundColor: "#19191a",
                color: "#fff",
                marginTop: "0",
                minWidth: "200px",
                width: dropdownRefs.current.get(route.name)?.offsetWidth,
              },
              "& .MuiMenuItem-root": {
                fontSize: "1.1rem",
                padding: "12px 24px",
                whiteSpace: "nowrap",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                },
              },
            }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            {route.dropdownItems.map((item) => (
              <MenuItem key={item.path} component={Link} to={item.path} onClick={handleMouseLeave}>
                {item.name}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      );
    }

    return (
      <Button key={route.path} component={Link} to={route.path} sx={buttonStyle}>
        {route.name}
      </Button>
    );
  };

  return (
    <>
      <AppBar sx={appBarStyle}>
        <Toolbar sx={toolbarStyle}>
          <Box
            sx={{
              flexGrow: 0,
              display: "flex",
              alignItems: "center",
              mr: { xs: 1, sm: 2, md: 3 },
            }}
          >
            <Button
              key={"/"}
              component={Link}
              to={"/"}
              sx={{
                ...buttonStyle,
                marginRight: 0,
              }}
            >
              <img className={styles.img} src={Logo} alt="MUGGLE AI LOGO" />
            </Button>
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 2, md: 3 },
            }}
          >
            {routes.map(renderRoute)}
          </Box>

          {isTablet ? (
            <IconButton
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={() => setMobileMenuOpen(true)}
              sx={{
                ml: { xs: 1, sm: 2 },
                padding: { xs: "4px", sm: "8px" },
                display: "flex",
              }}
            >
              <MenuIcon
                sx={{
                  fontSize: { xs: "1.5rem", sm: "2rem" },
                }}
              />
            </IconButton>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                ml: { xs: 1, sm: 2, md: 3 },
              }}
            >
              <LoginLayout />
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "240px", sm: "280px", md: "320px" },
            backgroundColor: "#19191a",
            color: "#fff",
          },
        }}
      >
        <Box
          sx={{
            mt: {
              xs: "56px",
              sm: "72px",
              md: "96px",
            },
          }}
        >
          {routes.map(renderMobileMenuItem)}
          <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
            <LoginLayout />
          </Box>
        </Box>
      </Drawer>

      {/* Add a toolbar placeholder with matching height */}
      <Toolbar
        sx={{
          height: {
            xs: "56px",
            sm: "72px",
            md: "96px",
          },
          minHeight: {
            xs: "56px",
            sm: "72px",
            md: "96px",
          },
        }}
      />
    </>
  );
};

export default NavigationBar;
